import type {List} from 'immutable';
import {Empty} from '@albert-io/json-api-framework/models/helpers';
import {GenericListCollectionModel} from 'resources/Generic/GenericCollection.model';
import type {GuideLevel} from 'resources/GuideLevel/GuideLevel.model';
import type {GuideLevelModelV2 as GuideLevelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import type {TagModelV1 as TagModel} from 'resources/GeneratedModels/Tag/TagModel.v1';
import type {QuestionType} from 'resources/Question/Question.model';
import type {AuthoringGuideLevelV1} from 'resources/GeneratedModels/AuthoringGuideLevel/AuthoringGuideLevelModel.v1';

/**
 * Describes the dynamic accessor surface that `this` exposes inside the
 * extension methods. These extensions are mixed (via `extend`) into the
 * generated `QuestionSetModelV1`, which is authored in untyped JS, so there is
 * no generated model type to inherit from here. This honest interface captures
 * exactly the accessors (base-model helpers and sibling extension methods) the
 * methods below rely on through `this`.
 */
interface QuestionSetExtensionsThis {
  // Base model (GenericModel/Immutable Record) helpers.
  get(key: string): any;
  getIn(keyPath: string[], notSetValue?: unknown): any;
  cache<T>(key: string, func: () => T): T;
  // Generated relationship accessors used by the extensions.
  getTags(): {getCollection(): List<TagModel>};
  getAuthoringGuideLevels(): List<AuthoringGuideLevelV1>;
  // Sibling extension methods referenced through `this`.
  getGuideLevels(): List<GuideLevel | GuideLevelV2>;
  getFreeGuideLevels(): List<GuideLevel | GuideLevelV2>;
  getAuthoringGuideLevelPath(): List<AuthoringGuideLevelV1> | null;
  getGuideLevelPath(): List<GuideLevelV2> | null;
  getQuestions(): List<QuestionType>;
  getQuestionByTitleSlug(slug: string): QuestionType;
  getQuestionIds(): List<string>;
  isMultiQuestionSet(): boolean;
}

/**
 * A question set's `questions` are only Question models once the relationship has been included
 * and resolved; before that the list holds bare records, which have `get` but none of the model's
 * accessors. Returns undefined when the entry carries no type at all.
 */
export function readQuestionType(question: unknown): string | undefined {
  if (!question || typeof question !== 'object') {
    return undefined;
  }
  const entry = question as {
    getType?: () => string;
    get?: (key: string) => unknown;
    question_type?: string;
  };
  if (typeof entry.getType === 'function') {
    return entry.getType();
  }
  if (typeof entry.get === 'function') {
    return entry.get('question_type') as string | undefined;
  }
  return entry.question_type;
}

export const questionSetExtensions = {
  getAssignmentPosition(assignmentId: string): number {
    return this.getIn(['relationshipMeta', 'assignment', assignmentId, 'position'], 0);
  },

  getTemplatePosition(templateId: string): number {
    return this.getIn(['relationshipMeta', 'template', templateId, 'position'], 0);
  },

  getFreeGuideLevels(): List<GuideLevel | GuideLevelV2> {
    // Immutable 3.8's typings declare List#filter as returning Iterable, but at
    // runtime it returns a List; cast to the concrete type the method promises.
    return this.getGuideLevels().filter((level) => {
      return level.isFree();
    }) as List<GuideLevel | GuideLevelV2>;
  },

  getGuideLevelPath(): List<GuideLevelV2> | null {
    if (this.getGuideLevels().isEmpty()) {
      return null;
    }
    return this.cache('getGuideLevelPath', () => {
      // Immutable 3.8 types sortBy as Iterable; at runtime it returns a List.
      return this.getGuideLevels().sortBy((guideLevel) =>
        guideLevel.getNlevel()
      ) as List<GuideLevelV2>;
    });
  },

  getAuthoringGuideLevelPath(): List<AuthoringGuideLevelV1> | null {
    if (this.getAuthoringGuideLevels().isEmpty()) {
      return null;
    }
    return this.cache('getAuthoringGuideLevelPath', () => {
      // Immutable 3.8 types sortBy as Iterable; at runtime it returns a List.
      return this.getAuthoringGuideLevels().sortBy((guideLevel) =>
        guideLevel.getNlevel()
      ) as List<AuthoringGuideLevelV1>;
    });
  },

  getGuideLevels(): List<GuideLevel | GuideLevelV2> {
    // The GuideLevelCollectionModel requires we call .getCollection() in order to get our data.
    // If the data is the empty symbol, we return an empty List().
    const guideLevels = this.get('guide_levels');
    if (guideLevels === Empty) {
      // `GenericListCollectionModel` is a factory function (not a class) that
      // returns a `List`; it returns the same object whether or not `new` is
      // used, so the cast here preserves the original runtime behavior.
      return GenericListCollectionModel() as List<GuideLevel | GuideLevelV2>;
    } else {
      return guideLevels.getCollection();
    }
  },

  getPosition(): number {
    return this.getIn(['meta', 'position'], 0);
  },

  getQuestionByTitleSlug(slug: string): QuestionType {
    return this.getQuestions().find((question) => {
      return question?.getTitleSlug() === slug;
    });
  },

  getQuestions(): List<QuestionType> {
    const func = () => this.get('questions');
    return this.cache('getQuestions', func);
  },

  getQuestionIds(): List<string> {
    // Immutable 3.8 types map as Iterable; at runtime it returns a List.
    const func = () => this.getQuestions().map((question) => question?.getId()) as List<string>;
    return this.cache('getQuestionIds', func);
  },

  getStudentAssignmentPosition(assignmentId: string): number {
    return this.getIn(['relationshipMeta', 'student_assignment', assignmentId, 'position'], 0);
  },

  getTagsSortedByName(): List<TagModel> {
    return this.cache('tagsSortedByName', () => {
      // Immutable 3.8 types sortBy as Iterable; at runtime it returns a List.
      return this.getTags()
        .getCollection()
        .sortBy((tag) => tag.getName()) as List<TagModel>;
    });
  },

  /**
   * Returns the question-set slug part from `slug_id` (shape:
   * `subject@question-set-slug`). Nullable — backend redacts `slug_id` on
   * locked questions (#19056); callers must treat a null return as "no
   * link target".
   */
  getURLSlugPart(): string | null {
    const slugId: string | null = this.get('slug_id');
    return slugId ? slugId.split('@')[1] : null;
  },

  isFree(): boolean {
    return this.cache('isFree', () => !this.getFreeGuideLevels().isEmpty());
  },

  isMultiQuestionSet(): boolean {
    return this.getQuestions().size > 1;
  },

  hasFreeResponseQuestions(): boolean {
    return this.getQuestions().some((q) => readQuestionType(q) === 'free-response');
  },

  hasMultipleFreeResponseQuestions(): boolean {
    return (
      this.isMultiQuestionSet() &&
      this.getQuestions().filter((q) => readQuestionType(q) === 'free-response').size > 1
    );
  }
} satisfies ThisType<QuestionSetExtensionsThis>;
